// Platform super-admin layout — deliberately SEPARATE from components/Layout.tsx
// (the tenant-agent sidebar), which reads the tenant AuthContext and would be
// wrong here. Minimal top-bar layout: brand mark + a persistent "PLATFORM"
// badge (so this area is never visually mistaken for a tenant view) + two nav
// links (Tenants, Billing, Payment incidents, Audit Log). Billing carries
// `end` because /super_admin/billing/incidents is a child route and would
// otherwise light up both entries at once + language/theme toggles + logout.

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Building2, ScrollText, CreditCard, AlertTriangle } from 'lucide-react';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { ThemeToggle } from './ThemeToggle';
import { ProjectLockup } from './ProjectLockup';

export function PlatformLayout() {
  const { t, i18n } = useTranslation();
  const { admin, logout } = usePlatformAuth();
  const navigate = useNavigate();

  function toggleLanguage() {
    i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');
  }

  async function handleLogout() {
    await logout();
    navigate('/super_admin/login', { replace: true });
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'flex items-center gap-2 px-3.5 py-2 rounded-brand-pill text-sm font-medium',
      'transition-colors duration-fast ease-standard',
      isActive
        ? 'bg-primary/10 text-primary'
        : 'text-secondary hover:text-primary hover:bg-surface-hover',
    ].join(' ');

  return (
    <div className="min-h-screen bg-canvas">
      <header
        className="sticky top-0 z-40 border-b border-default/70 bg-surface/95 backdrop-blur-xl"
        style={{ boxShadow: 'var(--shadow-sm)' }}
      >
        <div className="mx-auto flex h-16 max-w-[96rem] items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <ProjectLockup />
            {/* Persistent platform-scope badge — never let this area be
                mistaken for a tenant view at a glance. */}
            <span
              className="shrink-0 inline-flex items-center rounded-brand-pill px-2.5 py-1 text-[11px] font-bold tracking-wide"
              style={{ backgroundColor: 'var(--tint-info)', color: 'var(--status-info)' }}
            >
              {t('super_admin.badge')}
            </span>
          </div>

          <nav className="hidden sm:flex items-center gap-1">
            <NavLink to="/super_admin" end className={navLinkClass}>
              <Building2 size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{t('super_admin.nav_tenants')}</span>
            </NavLink>
            <NavLink to="/super_admin/billing" end className={navLinkClass}>
              <CreditCard size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{t('super_admin.nav_billing')}</span>
            </NavLink>
            <NavLink to="/super_admin/billing/incidents" className={navLinkClass}>
              <AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{t('super_admin.nav_incidents')}</span>
            </NavLink>
            <NavLink to="/super_admin/audit" className={navLinkClass}>
              <ScrollText size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{t('super_admin.nav_audit')}</span>
            </NavLink>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {admin && (
              <span className="hidden md:inline text-xs text-muted truncate max-w-[160px]" dir="ltr">
                {admin.email}
              </span>
            )}
            <ThemeToggle showLabel={false} />
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              className="h-8 px-3 text-xs font-medium rounded-brand-md
                border border-default text-secondary hover:text-primary
                hover:bg-surface-hover transition-colors duration-base ease-standard"
            >
              {t('lang_toggle')}
            </button>
            <button
              onClick={() => void handleLogout()}
              className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-brand-md
                border border-default text-secondary hover:text-primary
                hover:bg-surface-hover transition-colors duration-base ease-standard"
            >
              <LogOut size={14} strokeWidth={1.75} aria-hidden="true" />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>

        {/* Mobile nav row */}
        <nav className="mx-auto flex max-w-[96rem] items-center gap-1 overflow-x-auto px-4 pb-2 sm:hidden">
          <NavLink to="/super_admin" end className={navLinkClass}>
            <Building2 size={16} strokeWidth={1.75} aria-hidden="true" />
            <span>{t('super_admin.nav_tenants')}</span>
          </NavLink>
          <NavLink to="/super_admin/billing" end className={navLinkClass}>
            <CreditCard size={16} strokeWidth={1.75} aria-hidden="true" />
            <span>{t('super_admin.nav_billing')}</span>
          </NavLink>
          <NavLink to="/super_admin/billing/incidents" className={navLinkClass}>
            <AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" />
            <span>{t('super_admin.nav_incidents')}</span>
          </NavLink>
          <NavLink to="/super_admin/audit" className={navLinkClass}>
            <ScrollText size={16} strokeWidth={1.75} aria-hidden="true" />
            <span>{t('super_admin.nav_audit')}</span>
          </NavLink>
        </nav>
      </header>

      <main className="mx-auto w-full max-w-[96rem] px-4 py-6 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
